/**
 * SSE（Server-Sent Events）流式数据解析工具
 * 用于解析 AI 聊天接口的流式响应数据
 *
 * 支持两种格式：
 * 1. 标准 SSE：data: { ... } 格式
 * 2. ReadableStream：纯文本流
 */

import type { SSEEventChunk } from '@/service/chat';

/**
 * 解析单行 SSE 数据
 * 支持格式：data: {"type":"content","content":"hello"}
 *
 * @param line - 原始数据行
 * @returns 解析后的事件块，无效行返回 null
 */
export const parseSSELine = (line: string): SSEEventChunk | null => {
  const trimmed = line.trim();
  if (!trimmed || !trimmed.startsWith('data: ')) return null;

  const payload = trimmed.slice(6).trim();

  try {
    const parsed = JSON.parse(payload) as SSEEventChunk;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * 从 ReadableStream 读取 SSE 流并逐块回调
 * 使用 TextDecoder 解码二进制数据，处理跨行粘包
 * 每处理一条 chunk 都 yield 一次，确保 React 能逐块渲染
 *
 * @param reader - ReadableStream 读取器
 * @param onChunk - 每解析到一个事件块时回调
 * @param signal - AbortSignal 用于中断读取
 */
export const readSSEStream = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onChunk: (chunk: SSEEventChunk) => void,
  signal?: AbortSignal
): Promise<void> => {
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      if (signal?.aborted) break;

      const { done, value } = await reader.read();
      
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const chunk = parseSSELine(line);
        if (chunk) onChunk(chunk);
        // 让出主线程，使 React 能在每个 chunk 间刷新渲染
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }
    }

    if (buffer.trim()) {
      const chunk = parseSSELine(buffer);
      if (chunk) onChunk(chunk);
    }
  } catch (err) {
    if (!signal?.aborted) {
      console.error('SSE stream read error:', err);
      throw err;
    }
  }
};
